import { createDataTableColumnHelper, DataTable, Link } from "@beweegbaas/ui";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { RapportNotitiesField } from "#/modules/rapporten/components/field.rapport-notities";
import { getRapportStep3Notities } from "#/modules/rapporten/rapporten.functions";
import { useRapportenFlow } from "#/modules/rapporten/use-rapporten-flow";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/reports/flow/$id/step/3/$class",
)({
  loader: ({ params }) => getRapportStep3Notities({ data: { rapportId: params.id } }),
  component: ReportsStep3ClassPage,
  errorComponent: ReportsStep3ClassError,
});

type NotitieRow = Awaited<
  ReturnType<typeof getRapportStep3Notities>
>["notities"][number]["notities"][number];

const helper = createDataTableColumnHelper<NotitieRow>();

function ReportsStep3ClassPage() {
  const params = Route.useParams();
  const { rapport } = useRapportenFlow();
  const { notities } = Route.useLoaderData();
  const navigate = useNavigate();

  const currentClass = rapport?.klassen.find((klas) => klas.id === params.class);

  useEffect(() => {
    if (!rapport?.klassen?.length) {
      void navigate({
        to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/2",
        params: {
          locale: params.locale,
          schoolyear: params.schoolyear,
          school: params.school,
          id: params.id,
        },
        replace: true,
      });
      return;
    }

    if (!currentClass) {
      void navigate({
        to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/2",
        params: {
          locale: params.locale,
          schoolyear: params.schoolyear,
          school: params.school,
          id: params.id,
        },
        replace: true,
      });
    }
  }, [rapport, currentClass, navigate, params]);

  const columns = useMemo(
    () =>
      helper.columns([
        helper.display({
          id: "student",
          header: () => m["common.fields.student"](),
          meta: {
            cellClassName:
              "w-0 py-3 align-top font-medium sm:min-w-[300px] max-md:h-auto max-md:w-full max-md:pb-0",
          },
          cell: ({ row }) => (
            <Link
              variant="secondary"
              underline={true}
              to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
              params={{
                locale: params.locale,
                schoolyear: params.schoolyear,
                school: params.school,
                id: row.original.leerlingId,
              }}
            >
              {row.original.leerlingNaam}
            </Link>
          ),
        }),
        helper.display({
          id: "note",
          header: () => m["common.notitie"](),
          meta: { cellClassName: "py-3 max-md:h-auto max-md:w-full max-md:pt-2 max-md:pl-4" },
          cell: ({ row }) => (
            <RapportNotitiesField
              rapportId={params.id}
              leerlingId={row.original.leerlingId}
              value={row.original.notitie}
              className="w-full max-w-[65ch]"
              notitiesInRangeCount={row.original.notitiesInRangeCount}
              onOpen={() => {
                void navigate({
                  to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/3/$class/drawer/notities/$leerlingId",
                  params: {
                    locale: params.locale,
                    schoolyear: params.schoolyear,
                    school: params.school,
                    id: params.id,
                    class: params.class,
                    leerlingId: row.original.leerlingId,
                  },
                });
              }}
            />
          ),
        }),
      ]),
    [params, navigate],
  );

  if (!rapport?.klassen?.length || !currentClass) {
    return null;
  }

  const classNotities =
    notities.find((group) => group.klasNaam === currentClass.name)?.notities || [];

  return (
    <>
      <DataTable
        card
        aria-label={m["rapporten.notities.title"]()}
        columns={columns}
        data={classNotities}
        getRowId={(notitie) => notitie.leerlingId}
        // On mobile the two columns stack: leerling name above the notitie field. The whole
        // table chain switches to block/flex — flex rows inside table display overlap.
        className="max-md:[&_table]:block max-md:[&_tbody]:block max-md:[&_thead]:hidden max-md:[&_tr]:flex max-md:[&_tr]:flex-col max-md:[&_td]:w-full"
      />
      <Outlet />
    </>
  );
}

function ReportsStep3ClassError({ error }: { error: unknown }) {
  const routeParams = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={routeParams} />;
}
